var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { saveState } from '../helper/stateManagerHelper';
import { playAudio } from '../helper/audioHelper';
import { announce } from '../helper/announcer';
import customEventHandler from '../helper/customEvents';
import { bringManipulativesToFront } from '../helper/helper';
/**
 * Selection / drag overlay for a Coins & Counters group or pre-group
 * multi-select. Renders a thin bounding box that:
 *   • catches mousedown anywhere inside it (Pattern-Shapes parity — drag
 *     from the white space between coins, not just from a coin), and
 *   • drags every member together, leaving each coin's `data.center`
 *     intact so Ungroup preserves their original positions.
 *
 * Mounted from WhiteBoard only when the active selection is either a
 * formal `manipulativeGroup_` of coins, or a multi-select of 2+ coins
 * (lasso / shift-click). Otherwise individual `<CoinsAndCountersManipulative>`
 * instances handle their own drag.
 */
var PAD = 12;
var CoinsAndCountersGroupOverlay = function (_a) {
    var members = _a.members, groupId = _a.groupId, children = _a.children;
    var _b = useContext(DataContext), setManipulative = _b.setManipulative, setContextMenu = _b.setContextMenu, setSelectedTool = _b.setSelectedTool, selectedTool = _b.selectedTool;
    var isGroupActive = groupId ? selectedTool.includes(groupId) : true;
    // Group flip — used by (a) the click-no-drag branch in onUp and (b) the
    // 'flip' bottomPanel listener (right-click context menu → Flip). Each
    // flip-enabled member rolls its own random side (dice semantics — can
    // land on the same side); per-member animation is dispatched via
    // `coinsAndCountersFlipAnimate` so the spin runs even when the random
    // pick happens to match the current side.
    var triggerGroupFlip = function () {
        var flips = [];
        members.forEach(function (m) {
            var _a;
            var d = m.data;
            if (!d.flipEnabled)
                return;
            var current = (_a = d.currentSide) !== null && _a !== void 0 ? _a : 'front';
            var next = Math.random() < 0.5 ? 'front' : 'back';
            flips.push({ id: d.id, old: current, next: next });
        });
        if (flips.length === 0)
            return;
        flips.forEach(function (f) {
            var _a;
            saveState({
                type: f.id,
                id: (_a = f.id.split('_')[1]) !== null && _a !== void 0 ? _a : f.id,
                propertyName: 'currentSide',
                old: JSON.stringify(f.old),
                new: JSON.stringify(f.next),
            });
        });
        var flipMap = new Map(flips.map(function (f) { return [f.id, f.next]; }));
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var _a;
                return flipMap.has((_a = item.data) === null || _a === void 0 ? void 0 : _a.id)
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { currentSide: flipMap.get(item.data.id) }) }) : item;
            });
        });
        flips.forEach(function (f) {
            customEventHandler.dispatchEvent('coinsAndCountersFlipAnimate', {
                id: f.id,
                newSide: f.next,
            });
        });
        playAudio('flipCoinsAndCounter');
    };
    // Right-click → Flip menu item dispatches `bottomPanel: { id: 'flip' }`.
    // Listen here so a coin-group flip works from the menu the same way it
    // works when clicking the overlay without dragging.
    useEffect(function () {
        var onToolClick = function (evt) {
            if (evt.id !== 'flip')
                return;
            triggerGroupFlip();
        };
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [members]);
    var zoomLevel = globalStore.useStoreState(function (s) { return s.playerStore; }).zoomLevel;
    var _c = useState(false), isDragging = _c[0], setIsDragging = _c[1];
    // Ref on the outline div itself so drag can nudge the bbox along with the
    // members; otherwise the box stays anchored to the original (data.center)
    // bbox until setManipulative commits on mouseup, and the user sees the
    // members move while the outline stays put.
    var overlayRef = useRef(null);
    // Result of the drag-end reorder computed inside setManipulative's updater
    // below — read back right after, so saveState (a side effect) stays OUTSIDE
    // the updater, which React/StrictMode can invoke twice.
    var reorderResultRef = useRef(null);
    // iPad ghost-click guard — see CoinsAndCountersToolbar for the full
    // explanation. Without this, a single tap on the bbox would fire onDown
    // twice (touch + synthetic mouse) and trigger the group flip twice.
    var lastTouchAtRef = useRef(0);
    var longPressRef = useRef(null);
    // Captured at mousedown so window-level move/up listeners read fresh values.
    var dragRef = useRef(null);
    // Keyboard movement bookkeeping. startMap captures each member's pre-press
    // center once at the start of a key burst; curOffset accumulates the
    // delta across successive ticks so we know exactly what saveState's
    // old/new should hold per member when the debounce fires.
    var keyMoveStartRef = useRef(null);
    var keyMoveCurOffsetRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    // Commit any pending keyboard-move undo entry. Called from the debounce
    // timer (normal path) AND on unmount (so a fast Escape after arrow-key
    // movement doesn't drop the undo step).
    var commitPendingKeyboardMove = function () {
        var startMap = keyMoveStartRef.current;
        var offset = keyMoveCurOffsetRef.current;
        keyMoveStartRef.current = null;
        keyMoveCurOffsetRef.current = null;
        if (!startMap || !offset)
            return;
        if (offset.dx === 0 && offset.dy === 0)
            return;
        startMap.forEach(function (startCenter, mId) {
            var _a;
            saveState({
                type: mId,
                id: (_a = mId.split('_')[1]) !== null && _a !== void 0 ? _a : mId,
                propertyName: 'center',
                old: JSON.stringify(startCenter),
                new: JSON.stringify({
                    x: startCenter.x + offset.dx,
                    y: startCenter.y + offset.dy,
                }),
            });
        });
    };
    // Compute the bbox from the current members' centers + scaled dimensions.
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    members.forEach(function (m) {
        var _a, _b, _c, _d, _e, _f;
        var d = m.data;
        var w = ((_b = (_a = d.dimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0) * ((_c = d.scale) !== null && _c !== void 0 ? _c : 1);
        var h = ((_e = (_d = d.dimensions) === null || _d === void 0 ? void 0 : _d.height) !== null && _e !== void 0 ? _e : 0) * ((_f = d.scale) !== null && _f !== void 0 ? _f : 1);
        minX = Math.min(minX, d.center.x - w / 2);
        minY = Math.min(minY, d.center.y - h / 2);
        maxX = Math.max(maxX, d.center.x + w / 2);
        maxY = Math.max(maxY, d.center.y + h / 2);
    });
    var onMove = function (e) {
        var drag = dragRef.current;
        if (!drag)
            return;
        if (longPressRef.current) {
            clearTimeout(longPressRef.current);
            longPressRef.current = null;
        }
        var point = 'touches' in e ? e.touches[0] : e;
        var dx = (point.clientX - drag.clientX) / zoomLevel;
        var dy = (point.clientY - drag.clientY) / zoomLevel;
        if (!drag.hasMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
            drag.hasMoved = true;
            setIsDragging(true);
        }
        if (drag.hasMoved) {
            drag.movables.forEach(function (mv) {
                if (mv.el) {
                    mv.el.style.left = "".concat(mv.startCenter.x + dx, "px");
                    mv.el.style.top = "".concat(mv.startCenter.y + dy, "px");
                }
            });
            // Move the outline along with the members using transform so React's
            // declarative left/top (computed from stale data.center) doesn't
            // overwrite the imperative update when setIsDragging re-renders.
            if (overlayRef.current) {
                overlayRef.current.style.transform = "translate(".concat(dx, "px, ").concat(dy, "px)");
            }
        }
        if ('touches' in e)
            e.preventDefault();
    };
    var onUp = function (e) {
        var drag = dragRef.current;
        if (longPressRef.current) {
            clearTimeout(longPressRef.current);
            longPressRef.current = null;
        }
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchend', onUp);
        if (!drag)
            return;
        if (drag.hasMoved) {
            var point = 'changedTouches' in e
                ? e.changedTouches[0]
                : e;
            var dx_1 = (point.clientX - drag.clientX) / zoomLevel;
            var dy_1 = (point.clientY - drag.clientY) / zoomLevel;
            var updates = drag.movables.map(function (mv) { return ({
                id: mv.id,
                oldCenter: mv.startCenter,
                newCenter: {
                    x: mv.startCenter.x + dx_1,
                    y: mv.startCenter.y + dy_1,
                },
            }); });
            // One batched array (not a saveState call per coin) so the whole
            // group's drag undoes/redoes in a single step — a separate saveState
            // per member meant a 3-coin group drag needed 4 separate Undo presses
            // (3 centers + 1 reorder) to fully revert one gesture.
            var centerEntries = updates.map(function (u) {
                var _a;
                return ({
                    type: u.id,
                    id: (_a = u.id.split('_')[1]) !== null && _a !== void 0 ? _a : u.id,
                    propertyName: 'center',
                    old: JSON.stringify(u.oldCenter),
                    new: JSON.stringify(u.newCenter),
                });
            });
            var updateMap_1 = new Map(updates.map(function (u) { return [u.id, u.newCenter]; }));
            // Center update AND the drag-end reorder are done in ONE setManipulative
            // call (not two chained ones) — React only eagerly/synchronously runs a
            // state updater when it's the FIRST pending update for that state in
            // this batch; a second, later setManipulative call to the same state
            // is deferred to the actual render pass, so reorderResultRef would
            // never be populated in time for the synchronous read right after it
            // (this silently dropped every 'reorder' saveState call).
            var movedIds_1 = drag.movables.map(function (mv) { return mv.id; });
            reorderResultRef.current = null;
            setManipulative(function (prev) {
                var withCenters = prev.map(function (item) {
                    var _a;
                    return updateMap_1.has((_a = item.data) === null || _a === void 0 ? void 0 : _a.id)
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: updateMap_1.get(item.data.id) }) }) : item;
                });
                var beforeIds = withCenters.map(function (item) { var _a, _b; return (_b = (_a = item.data) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : ''; });
                var reordered = bringManipulativesToFront(withCenters, movedIds_1);
                var afterIds = reordered.map(function (item) { var _a, _b; return (_b = (_a = item.data) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : ''; });
                if (JSON.stringify(beforeIds) !== JSON.stringify(afterIds)) {
                    reorderResultRef.current = {
                        oldIds: JSON.stringify(beforeIds),
                        newIds: JSON.stringify(afterIds),
                    };
                }
                return reordered;
            });
            var readReorderResult = function () { return reorderResultRef.current; };
            var reorderResult = readReorderResult();
            var allEntries = reorderResult
                ? __spreadArray(__spreadArray([], centerEntries, true), [
                    {
                        type: groupId !== null && groupId !== void 0 ? groupId : movedIds_1[0],
                        id: groupId !== null && groupId !== void 0 ? groupId : movedIds_1[0],
                        propertyName: 'reorder',
                        old: reorderResult.oldIds,
                        new: reorderResult.newIds,
                    },
                ], false) : centerEntries;
            saveState(allEntries);
        }
        else {
            // ---- Click on the group overlay without drag ----
            // Spec: "If there are two-sided Counters that are grouped (and flipping
            // is turned on), clicking within the shaded space around the Counters
            // cause the Counters to randomly flip and a sound is heard."
            triggerGroupFlip();
        }
        dragRef.current = null;
        if (overlayRef.current)
            overlayRef.current.style.transform = '';
        setIsDragging(false);
    };
    var onDown = function (e) {
        // Right-click is reserved for the context menu — don't treat it as a
        // drag-start / click-flip. Without this guard, the right-click's
        // implicit mouseup would fall into onUp's click-no-drag branch and
        // trigger a group flip in addition to opening the menu.
        if ('button' in e && e.button === 2)
            return;
        // iPad ghost-click guard — ignore synthetic mousedown that follows
        // a real touchstart by ~300 ms.
        var isTouch = 'touches' in e;
        if (isTouch) {
            lastTouchAtRef.current = Date.now();
        }
        else if (Date.now() - lastTouchAtRef.current < 500) {
            return;
        }
        // Don't start a drag if the group is locked.
        if (members.every(function (m) { var _a, _b, _c, _d; return ((_b = (_a = m.data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.positioning) || ((_d = (_c = m.data) === null || _c === void 0 ? void 0 : _c.lock) === null || _d === void 0 ? void 0 : _d.editing); }))
            return;
        // Prevent the canvas's own selection-clear handler from firing — the
        // user grabbed the group, they shouldn't lose it on mouseup-without-drag.
        e.stopPropagation();
        var point = 'touches' in e ? e.touches[0] : e;
        dragRef.current = {
            clientX: point.clientX,
            clientY: point.clientY,
            hasMoved: false,
            movables: members
                .filter(function (m) { return !!m.data.id; })
                .map(function (m) { return ({
                id: m.data.id,
                startCenter: __assign({}, m.data.center),
                el: document.querySelector("[data-id=\"".concat(m.data.id, "\"]")),
            }); }),
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchend', onUp);
        if ('touches' in e)
            e.preventDefault();
    };
    // On iPad, iOS Safari does not fire onContextMenu on long-press.
    // Start a 600ms timer; cancel it if the user moves (drag) or lifts early.
    var handleOverlayTouchStart = function (e) {
        var clientX = e.touches[0].clientX;
        var clientY = e.touches[0].clientY;
        longPressRef.current = setTimeout(function () {
            var _a, _b, _c, _d;
            longPressRef.current = null;
            dragRef.current = null;
            setIsDragging(false);
            var gId = (_b = (_a = members[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.groupId;
            var target = (_d = gId !== null && gId !== void 0 ? gId : (_c = members[0]) === null || _c === void 0 ? void 0 : _c.data.id) !== null && _d !== void 0 ? _d : '';
            if (!target)
                return;
            setContextMenu({ x: clientX, y: clientY, manipulativeId: target });
        }, 600);
        onDown(e);
    };
    var onKeyDown = function (e) {
        var _a, _b, _c, _d, _e;
        // Escape → deselect group. Flush any in-flight keyboard-move undo first
        // so the user doesn't lose their work in the 500 ms before the debounce.
        if (e.key === 'Escape') {
            e.preventDefault();
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            commitPendingKeyboardMove();
            setSelectedTool([]);
            return;
        }
        // Enter / Space / ContextMenu key → open context menu (matches single-counter
        // and PatternShapesSelectionGroup keyboard behaviour). Flip is accessible
        // via the Flip item in the context menu; click-without-drag still flips on mouse.
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10') ||
            e.key === 'F10') {
            e.preventDefault();
            var gId = (_b = (_a = members[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.groupId;
            var target = (_d = gId !== null && gId !== void 0 ? gId : (_c = members[0]) === null || _c === void 0 ? void 0 : _c.data.id) !== null && _d !== void 0 ? _d : '';
            if (!target)
                return;
            var rect = (_e = overlayRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect();
            if (rect) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: target });
            }
            return;
        }
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            // Don't move if the group is locked.
            if (members.every(function (m) { var _a, _b, _c, _d; return ((_b = (_a = m.data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.positioning) || ((_d = (_c = m.data) === null || _c === void 0 ? void 0 : _c.lock) === null || _d === void 0 ? void 0 : _d.editing); }))
                return;
            // Capture each member's start center once per key burst.
            if (!keyMoveStartRef.current) {
                var startMap_1 = new Map();
                members.forEach(function (m) {
                    var d = m.data;
                    if (d.id)
                        startMap_1.set(d.id, __assign({}, d.center));
                });
                keyMoveStartRef.current = startMap_1;
                keyMoveCurOffsetRef.current = { dx: 0, dy: 0 };
            }
            var step = e.shiftKey ? 50 : 10;
            var scaledStep = step / zoomLevel;
            var offset_1 = keyMoveCurOffsetRef.current;
            var delta = {
                ArrowUp: { x: 0, y: -scaledStep },
                ArrowDown: { x: 0, y: scaledStep },
                ArrowLeft: { x: -scaledStep, y: 0 },
                ArrowRight: { x: scaledStep, y: 0 },
            };
            offset_1.dx += delta[e.key].x;
            offset_1.dy += delta[e.key].y;
            // Apply the cumulative offset to each member's center. Same model as
            // the per-counter handler: setManipulative on every tick (cheap
            // because the members list is small for a group) so the user sees
            // continuous motion.
            var startMap_2 = keyMoveStartRef.current;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    var sc = startMap_2 === null || startMap_2 === void 0 ? void 0 : startMap_2.get((_a = item.data) === null || _a === void 0 ? void 0 : _a.id);
                    if (sc) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: sc.x + offset_1.dx, y: sc.y + offset_1.dy } }) });
                    }
                    return item;
                });
            });
            // Debounce: one undo entry per member + one announcement per burst.
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = setTimeout(function () {
                var _a, _b, _c;
                var finalOffset = keyMoveCurOffsetRef.current;
                var finalStartMap = keyMoveStartRef.current;
                commitPendingKeyboardMove();
                if (!finalOffset || !finalStartMap)
                    return;
                var dx = Math.round(finalOffset.dx);
                var dy = Math.round(finalOffset.dy);
                var directions = [];
                if (dy < 0)
                    directions.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                if (dy > 0)
                    directions.push("".concat(t('text.down'), " ").concat(dy));
                if (dx < 0)
                    directions.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                if (dx > 0)
                    directions.push("".concat(t('text.right'), " ").concat(dx));
                if (directions.length === 0)
                    return;
                // The group has no single "position" — use the first member's new
                // center as the reported coordinate (a representative point users
                // can sanity-check rather than the bbox top-left, which can drift
                // as members enter/leave).
                var firstId = (_a = members[0]) === null || _a === void 0 ? void 0 : _a.data.id;
                var firstStart = firstId ? finalStartMap.get(firstId) : undefined;
                var repX = Math.round(((_b = firstStart === null || firstStart === void 0 ? void 0 : firstStart.x) !== null && _b !== void 0 ? _b : 0) + finalOffset.dx);
                var repY = Math.round(((_c = firstStart === null || firstStart === void 0 ? void 0 : firstStart.y) !== null && _c !== void 0 ? _c : 0) + finalOffset.dy);
                announce(t('text.manipulativeMoved', {
                    name: t('text.coinsAndCountersGroupLabel', { count: members.length }),
                    direction: directions.join(" ".concat(t('text.and'), " ")),
                    x: repX,
                    y: repY,
                }));
            }, 500);
        }
    };
    // Focus restore after grouping via context menu picker or duplicate.
    // The picker / duplicate flow dispatches 'focusCcGroup' with a 150ms delay
    // (to beat the menu's focus-restore timer) and the overlay responds by
    // focusing itself — same pattern as PatternShapesSelectionGroup.
    useEffect(function () {
        if (!groupId)
            return;
        var handler = function (data) {
            var _a;
            if (data.groupId !== groupId)
                return;
            (_a = overlayRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        };
        customEventHandler.addEventListener('focusCcGroup', handler);
        return function () { return customEventHandler.removeEventListener('focusCcGroup', handler); };
    }, [groupId]);
    // Cleanup on unmount (e.g. user ungroups mid-drag, or Escape clears
    // selection). Flushes pending keyboard-move undo so a fast Escape doesn't
    // swallow the user's work.
    useEffect(function () {
        return function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchend', onUp);
            if (keyMoveSaveTimer.current) {
                clearTimeout(keyMoveSaveTimer.current);
                commitPendingKeyboardMove();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Compose aria-label. The overlay only mounts when the selection is
    // active, so "selected" is implicit and omitted.
    var buildOverlayAriaLabel = function () {
        var parts = [
            t('text.coinsAndCountersGroupLabel', { count: members.length }),
        ];
        var allLocked = members.every(function (m) {
            var _a;
            var lk = (_a = m.data) === null || _a === void 0 ? void 0 : _a.lock;
            return (lk === null || lk === void 0 ? void 0 : lk.positioning) || (lk === null || lk === void 0 ? void 0 : lk.editing);
        });
        if (allLocked)
            parts.push(t('text.locked'));
        if (!allLocked)
            parts.push(t('text.arrowKeysToMoveAll'));
        parts.push(t('text.escapeToDeselect'));
        return parts.join(', ');
    };
    return (_jsxs("div", { style: { position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }, children: [children, _jsx("div", { ref: overlayRef, className: "ccGroupOutline ".concat(isDragging ? 'ccGroupOutlineDragging' : '', " ").concat(!isGroupActive ? 'ccGroupOutline--inactive' : ''), "data-group-id": groupId !== null && groupId !== void 0 ? groupId : '', style: __assign({ position: 'absolute', left: "".concat(minX - PAD, "px"), top: "".concat(minY - PAD, "px"), width: "".concat(maxX - minX + PAD * 2, "px"), height: "".concat(maxY - minY + PAD * 2, "px"), pointerEvents: isGroupActive ? 'auto' : 'none' }, (zoomLevel <= 0.5 && { border: '0.1875rem solid #333' })), onMouseDown: isGroupActive ? onDown : undefined, onTouchStart: isGroupActive ? handleOverlayTouchStart : undefined, onKeyDown: onKeyDown, tabIndex: 0, role: "group", "aria-label": buildOverlayAriaLabel(), onFocus: function () {
                    if (groupId && !selectedTool.includes(groupId))
                        setSelectedTool([groupId]);
                }, onClick: function (e) {
                    var _a, _b, _c, _d, _e;
                    // VoiceOver on iPad fires a synthetic click (detail === 0) on double-tap.
                    if (e.detail !== 0)
                        return;
                    var gId = (_b = (_a = members[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.groupId;
                    var target = (_d = gId !== null && gId !== void 0 ? gId : (_c = members[0]) === null || _c === void 0 ? void 0 : _c.data.id) !== null && _d !== void 0 ? _d : '';
                    if (!target)
                        return;
                    var rect = (_e = overlayRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect();
                    if (rect) {
                        setContextMenu({ x: rect.right, y: rect.top, manipulativeId: target });
                    }
                }, onContextMenu: function (e) {
                    var _a, _b, _c, _d, _e;
                    e.preventDefault();
                    var gId = (_b = (_a = members[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.groupId;
                    var target = (_d = gId !== null && gId !== void 0 ? gId : (_c = members[0]) === null || _c === void 0 ? void 0 : _c.data.id) !== null && _d !== void 0 ? _d : '';
                    if (!target)
                        return;
                    var rect = (_e = overlayRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect();
                    if (rect) {
                        setContextMenu({
                            x: rect.right,
                            y: rect.top,
                            manipulativeId: target,
                        });
                    }
                } })] }));
};
export default CoinsAndCountersGroupOverlay;
